/**
 * Alpha2 codes of countries that have a registered ISO 13616 IBAN format.
 *
 * Used to decide whether the single account-number field should be collected and validated
 * as an IBAN or as a domestic account number for the selected bank country.
 *
 * This is deliberately not the SEPA participant list: SEPA is an EPC membership scheme and the
 * two sets differ in both directions — TR, IL and AE issue IBANs without being SEPA members.
 *
 * Source: SWIFT IBAN Registry (ISO 13616 registration authority), release 99 — December 2024.
 * Release 101 (December 2025) changed only the Portuguese BBAN layout and added no countries.
 * Update if a national standards body registers a new format.
 */
export const IBAN_COUNTRY_ALPHA2_CODES: ReadonlySet<string> = new Set<string>([
  "AD", // Andorra
  "AE", // United Arab Emirates
  "AL", // Albania
  "AT", // Austria
  "AZ", // Azerbaijan
  "BA", // Bosnia and Herzegovina
  "BE", // Belgium
  "BG", // Bulgaria
  "BH", // Bahrain
  "BI", // Burundi
  "BR", // Brazil
  "BY", // Belarus
  "CH", // Switzerland
  "CR", // Costa Rica
  "CY", // Cyprus
  "CZ", // Czechia
  "DE", // Germany
  "DJ", // Djibouti
  "DK", // Denmark
  "DO", // Dominican Republic
  "EE", // Estonia
  "EG", // Egypt
  "ES", // Spain
  "FI", // Finland
  "FK", // Falkland Islands
  "FO", // Faroe Islands
  "FR", // France
  "GB", // United Kingdom
  "GE", // Georgia
  "GI", // Gibraltar
  "GL", // Greenland
  "GR", // Greece
  "GT", // Guatemala
  "HN", // Honduras
  "HR", // Croatia
  "HU", // Hungary
  "IE", // Ireland
  "IL", // Israel
  "IQ", // Iraq
  "IS", // Iceland
  "IT", // Italy
  "JO", // Jordan
  "KW", // Kuwait
  "KZ", // Kazakhstan
  "LB", // Lebanon
  "LC", // Saint Lucia
  "LI", // Liechtenstein
  "LT", // Lithuania
  "LU", // Luxembourg
  "LV", // Latvia
  "LY", // Libya
  "MC", // Monaco
  "MD", // Moldova
  "ME", // Montenegro
  "MK", // North Macedonia
  "MN", // Mongolia
  "MR", // Mauritania
  "MT", // Malta
  "MU", // Mauritius
  "NI", // Nicaragua
  "NL", // Netherlands
  "NO", // Norway
  "OM", // Oman
  "PK", // Pakistan
  "PL", // Poland
  "PS", // Palestinian territories
  "PT", // Portugal
  "QA", // Qatar
  "RO", // Romania
  "RS", // Serbia
  "RU", // Russia
  "SA", // Saudi Arabia
  "SC", // Seychelles
  "SD", // Sudan
  "SE", // Sweden
  "SI", // Slovenia
  "SK", // Slovakia
  "SM", // San Marino
  "SO", // Somalia
  "ST", // Sao Tome and Principe
  "SV", // El Salvador
  "TL", // Timor-Leste
  "TN", // Tunisia
  "TR", // Turkey
  "UA", // Ukraine
  "VA", // Vatican City
  "VG", // Virgin Islands, British
  "XK", // Kosovo
  "YE", // Yemen
]);

export const isIbanCountry = (alpha2code?: string | null): boolean =>
  !!alpha2code && IBAN_COUNTRY_ALPHA2_CODES.has(alpha2code);
